import type { ApolloCache } from '@apollo/client'

import {
  ShiftManagementCoverageSectionDocument,
  ShiftManagementUnassignedShiftFragment,
} from 'generated/resources/graphql'

import moment from 'moment'

interface UpdateUnassignedShiftsCoverageCacheParams {
  cache: ApolloCache<unknown>
  startAt: string
  endAt: string
  locationIds: string[]
  unassignedShifts: ShiftManagementUnassignedShiftFragment[]
}

const updateUnassignedShiftsCoverageCache = ({
  cache,
  startAt,
  endAt,
  locationIds,
  unassignedShifts,
}: UpdateUnassignedShiftsCoverageCacheParams) => {
  if (unassignedShifts.length === 0) return

  // we need to update the query for the current location and the current period only.
  const currentLocationId = locationIds.at(0)
  const shiftsForCurrentLocation = currentLocationId
    ? unassignedShifts.filter((unassignedShift) => {
        const belongsToCurrentLocation =
          Number(unassignedShift.locationId) === Number(currentLocationId)
        const overlapsWithCurrentView =
          moment(unassignedShift.startAt).isBefore(moment(endAt).endOf('day')) &&
          moment(unassignedShift.endAt).isAfter(moment(startAt).startOf('day'))

        return belongsToCurrentLocation && overlapsWithCurrentView
      })
    : unassignedShifts

  if (shiftsForCurrentLocation.length === 0) return

  cache.updateQuery(
    {
      query: ShiftManagementCoverageSectionDocument,
      variables: {
        startAt,
        endAt,
        locationId: currentLocationId,
      },
    },
    (previousData) => {
      if (!previousData?.shiftManagement) return previousData
      const previousUnassignedShifts = previousData?.shiftManagement?.unassignedShifts || []
      const mergedUnassignedShifts = Array.from(
        new Map(
          [...previousUnassignedShifts, ...shiftsForCurrentLocation].map((unassignedShift) => [
            unassignedShift.id,
            unassignedShift,
          ])
        ).values()
      )

      return {
        ...previousData,
        shiftManagement: {
          ...previousData.shiftManagement,
          unassignedShifts: mergedUnassignedShifts,
        },
      }
    }
  )
}

export { updateUnassignedShiftsCoverageCache }
